import React, { useState } from 'react';
import { 
  ArrowLeft, 
  Search, 
  RefreshCw, 
  Download, 
  Calendar, 
  ExternalLink, 
  Check, 
  X, 
  Loader2, 
  CheckCircle2,
  Filter,
  Layers,
  Zap,
  ShieldCheck,
  CreditCard,
  AlertCircle,
  ArrowRight,
  Plus,
  Building2,
  MapPin,
  Globe,
  FileText,
  User,
  XCircle,
  ShieldAlert,
  GitBranch,
  BellRing,
  History,
  ChevronRight,
  Edit2
} from 'lucide-react';

interface LEIRenewal {
  id: string;
  date: string;
  type: 'Annual' | 'Bulk' | 'Multi-Year';
  status: 'Completed' | 'Pending';
}

interface AssociatedEntity {
  name: string;
  lei: string;
  relationship: 'Parent' | 'Subsidiary';
}

interface LEIEntity {
  id: string;
  name: string;
  code: string;
  expiry: string;
  days: number;
  status: 'Issued' | 'Lapsed' | 'Expired';
  reminders: boolean;
  jurisdiction: string;
  renewalHistory: LEIRenewal[];
  associatedEntities: AssociatedEntity[];
}

const MOCK_LEIS: LEIEntity[] = [
  { 
    id: '1', 
    name: 'FinTech Solutions S.A.', 
    code: '213800XJ6XF9M1D3D612', 
    expiry: '2023-11-01', 
    days: 7, 
    status: 'Lapsed',
    reminders: true,
    jurisdiction: 'FR',
    renewalHistory: [
      { id: 'R1', date: '2022-11-01', type: 'Annual', status: 'Completed' },
      { id: 'R2', date: '2021-11-01', type: 'Annual', status: 'Completed' }
    ],
    associatedEntities: [
      { name: 'FinTech Holdings GMBH', lei: '984500ZJ9XH0O3F5F834', relationship: 'Parent' }
    ]
  },
  { 
    id: '2', 
    name: 'Global Assets Corp', 
    code: '549300H0O7O7F3V7S767', 
    expiry: '2023-11-24', 
    days: 30, 
    status: 'Issued',
    reminders: true,
    jurisdiction: 'US',
    renewalHistory: [
      { id: 'R3', date: '2022-11-24', type: 'Annual', status: 'Completed' }
    ],
    associatedEntities: []
  },
  { 
    id: '3', 
    name: 'Alpha Banking Group', 
    code: 'W226V9S7Y7M7L7K7J755', 
    expiry: '2024-05-12', 
    days: 180, 
    status: 'Issued',
    reminders: true,
    jurisdiction: 'GB',
    renewalHistory: [
      { id: 'R4', date: '2023-05-12', type: 'Multi-Year', status: 'Completed' }
    ],
    associatedEntities: [
      { name: 'Alpha Retail Ltd', lei: 'R446B3V7X7Z7W7Q7P744', relationship: 'Subsidiary' },
      { name: 'Alpha Wealth Management', lei: '335800YJ7XG8N2E4E723', relationship: 'Subsidiary' }
    ]
  },
  { 
    id: '4', 
    name: 'Sterling Trust Ltd', 
    code: 'R446B3V7X7Z7W7Q7P744', 
    expiry: '2023-10-15', 
    days: -10, 
    status: 'Expired',
    reminders: false,
    jurisdiction: 'GB',
    renewalHistory: [],
    associatedEntities: [
      { name: 'Alpha Banking Group', lei: 'W226V9S7Y7M7L7K7J755', relationship: 'Parent' }
    ]
  }
];

// Define components used within LEIView before their usage
const StatusDetailRow: React.FC<{ label: string, value: string, icon: React.ReactNode }> = ({ label, value, icon }) => (
  <div className="flex items-center justify-between py-1 border-b border-slate-50 last:border-0 pb-3">
    <div className="flex items-center space-x-2 text-slate-400">
       {icon}
       <span className="text-[10px] font-black uppercase tracking-widest">{label}</span>
    </div>
    <span className="text-xs font-black text-slate-900">{value}</span>
  </div>
);

const RegistryStat: React.FC<{ label: string, value: string | number, icon: React.ReactNode, color: string }> = ({ label, value, icon, color }) => {
  const colorMap: Record<string, string> = {
    blue: 'bg-blue-50 text-blue-600 border-blue-100',
    emerald: 'bg-emerald-50 text-emerald-600 border-emerald-100',
    amber: 'bg-amber-50 text-amber-600 border-amber-100',
    rose: 'bg-rose-50 text-rose-600 border-rose-100',
  };

  return (
    <div className="bg-white p-6 rounded-[2rem] border border-slate-200 shadow-sm hover:shadow-md transition-all">
      <div className={`w-10 h-10 rounded-2xl flex items-center justify-center mb-4 border ${colorMap[color]}`}>
        {icon}
      </div>
      <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">{label}</p>
      <p className={`text-3xl font-black text-slate-900`}>{value}</p>
    </div>
  );
};

export const LEIView: React.FC<{ onBack: () => void }> = ({ onBack }) => {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [processingStep, setProcessingStep] = useState<number | null>(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [isRegistering, setIsRegistering] = useState(false);
  const [detailedEntity, setDetailedEntity] = useState<LEIEntity | null>(null);

  // Form State for Registration
  const [formName, setFormName] = useState('');
  const [formJurisdiction, setFormJurisdiction] = useState('GB');
  const [formRegNo, setFormRegNo] = useState('');

  const toggleSelect = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedIds(newSelected);
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === MOCK_LEIS.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(MOCK_LEIS.map(l => l.id)));
    }
  };

  const handleUpdateStatus = (newStatus: 'Issued' | 'Lapsed' | 'Expired') => {
    if (!detailedEntity) return;
    setDetailedEntity({ ...detailedEntity, status: newStatus });
  };

  const toggleReminders = () => {
    if (!detailedEntity) return;
    setDetailedEntity({ ...detailedEntity, reminders: !detailedEntity.reminders });
  };

  const startRenewalFlow = () => {
    setShowConfirm(true);
  };

  const executeRenewal = () => {
    setShowConfirm(false);
    setProcessingStep(1);
    setTimeout(() => setProcessingStep(2), 1000);
    setTimeout(() => setProcessingStep(3), 2000);
    setTimeout(() => {
      setProcessingStep(null);
      setShowSuccess(true);
    }, 3000);
  };

  const executeRegistration = () => {
    setIsRegistering(false);
    setProcessingStep(1);
    setTimeout(() => setProcessingStep(2), 1200);
    setTimeout(() => setProcessingStep(3), 2400);
    setTimeout(() => {
      setProcessingStep(null);
      setShowSuccess(true);
    }, 3500);
  };

  const resetSelection = () => {
    setSelectedIds(new Set());
    setShowSuccess(false);
    setDetailedEntity(null);
  };

  const selectedEntities = MOCK_LEIS.filter(l => selectedIds.has(l.id));

  // If detailed view is open
  if (detailedEntity) {
    return (
      <div className="p-6 space-y-8 animate-in slide-in-from-right duration-500 max-w-7xl mx-auto">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button onClick={() => setDetailedEntity(null)} className="p-3 bg-white border border-slate-200 rounded-2xl hover:bg-slate-100 transition-all text-slate-600 shadow-sm">
              <ArrowLeft size={20} />
            </button>
            <div>
              <h1 className="text-2xl font-black text-slate-900 tracking-tight">{detailedEntity.name}</h1>
              <p className="text-sm text-slate-500 font-medium uppercase tracking-widest text-[10px] font-mono">{detailedEntity.code}</p>
            </div>
          </div>
          <div className="flex space-x-3">
             <button className="bg-white border border-slate-200 text-slate-700 p-3 rounded-2xl shadow-sm hover:bg-slate-50"><Download size={20} /></button>
             <button 
              onClick={() => { setProcessingStep(1); setTimeout(() => { setProcessingStep(null); setShowSuccess(true); }, 2000); }}
              className="bg-blue-600 text-white px-8 py-3 rounded-2xl text-[11px] font-black uppercase tracking-widest hover:bg-blue-700 shadow-xl shadow-blue-500/20 active:scale-95 flex items-center space-x-2"
             >
                <RefreshCw size={14} />
                <span>Force Manual Sync</span>
             </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
           {/* Left Column: Core Info & Status Management */}
           <div className="lg:col-span-4 space-y-6">
              <div className="bg-white p-8 rounded-[2.5rem] border border-slate-200 shadow-sm">
                 <div className="flex items-center justify-between mb-8">
                    <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Registry Management</h4>
                    <span className="bg-emerald-50 text-emerald-600 text-[9px] font-black px-2 py-0.5 rounded uppercase border border-emerald-100">Live Node</span>
                 </div>
                 
                 <div className="space-y-6">
                    <div>
                      <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest block mb-2">Entity Status</label>
                      <div className="flex bg-slate-50 p-1.5 rounded-2xl border border-slate-100">
                        {(['Issued', 'Lapsed', 'Expired'] as const).map((s) => (
                          <button
                            key={s}
                            onClick={() => handleUpdateStatus(s)}
                            className={`flex-1 py-2 rounded-xl text-[10px] font-black uppercase transition-all ${
                              detailedEntity.status === s 
                                ? detailedEntity.status === 'Issued' ? 'bg-emerald-500 text-white shadow-lg' :
                                  detailedEntity.status === 'Lapsed' ? 'bg-amber-500 text-white shadow-lg' :
                                  'bg-rose-500 text-white shadow-lg'
                                : 'text-slate-400 hover:text-slate-600'
                            }`}
                          >
                            {s}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className="grid grid-cols-1 gap-4">
                       <StatusDetailRow label="Registry Expiry" value={detailedEntity.expiry} icon={<Calendar size={12}/>} />
                       <StatusDetailRow label="Jurisdiction" value={detailedEntity.jurisdiction} icon={<Globe size={12}/>} />
                    </div>

                    <div className="pt-6 border-t border-slate-50 flex items-center justify-between">
                       <div>
                          <p className="text-sm font-black text-slate-900 leading-none mb-1">Renewal Reminders</p>
                          <p className="text-[10px] text-slate-400 font-bold uppercase">Automated 30d/7d/1d queue</p>
                       </div>
                       <button 
                        onClick={toggleReminders}
                        className={`w-12 h-6 rounded-full relative transition-all ${detailedEntity.reminders ? 'bg-blue-600' : 'bg-slate-200'}`}
                       >
                         <div className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow-sm transition-all ${detailedEntity.reminders ? 'left-7' : 'left-1'}`}></div>
                       </button>
                    </div>
                 </div>
              </div>

              <div className="bg-slate-900 p-8 rounded-[2.5rem] text-white shadow-2xl relative overflow-hidden group">
                 <h4 className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-4 flex items-center">
                    <ShieldAlert size={14} className="mr-2 text-rose-400" /> Compliance Note
                 </h4>
                 <p className="text-xs font-medium leading-relaxed opacity-80 mb-6">
                   This entity is currently flagged for an expired LEI code in the {detailedEntity.jurisdiction} jurisdiction. Trading activities may be restricted in EU/UK regions.
                 </p>
                 <button className="w-full py-3 bg-white/10 border border-white/10 rounded-xl text-[10px] font-black uppercase hover:bg-white/20 transition-all flex items-center justify-center">
                   Generate Resolution Roadmap
                 </button>
                 <div className="absolute -bottom-8 -right-8 w-32 h-32 bg-rose-600 rounded-full blur-[60px] opacity-10"></div>
              </div>
           </div>

           {/* Right Column: Structure & History */}
           <div className="lg:col-span-8 space-y-8">
              <div className="bg-white p-10 rounded-[3rem] border border-slate-200 shadow-sm">
                 <div className="flex items-center justify-between mb-8">
                    <h3 className="text-xs font-black text-slate-900 uppercase tracking-widest flex items-center">
                      <GitBranch size={16} className="mr-2 text-blue-600" /> Associated Entities & Corporate Structure
                    </h3>
                    <button className="p-2 hover:bg-slate-50 rounded-xl text-blue-600 transition-all">
                       <Edit2 size={16} />
                    </button>
                 </div>

                 {detailedEntity.associatedEntities.length === 0 ? (
                    <div className="py-12 text-center bg-slate-50 rounded-[2rem] border border-dashed border-slate-200 opacity-50">
                       <Building2 size={32} className="mx-auto mb-3 text-slate-400" />
                       <p className="text-[10px] font-black uppercase tracking-widest">No legal relationship mapped</p>
                    </div>
                 ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                       {detailedEntity.associatedEntities.map((entity, i) => (
                          <div key={i} className="p-5 bg-slate-50 border border-slate-100 rounded-2xl flex items-center justify-between group hover:bg-white hover:border-blue-200 transition-all">
                             <div className="flex items-center space-x-4">
                                <div className="w-10 h-10 bg-white rounded-xl shadow-sm flex items-center justify-center text-blue-600">
                                   <Building2 size={18} />
                                </div>
                                <div>
                                   <p className="text-xs font-black text-slate-900 leading-none mb-1">{entity.name}</p>
                                   <p className="text-[9px] font-mono text-slate-400 uppercase">{entity.lei}</p>
                                </div>
                             </div>
                             <span className={`text-[8px] font-black uppercase px-2 py-0.5 rounded shadow-sm border ${
                                entity.relationship === 'Parent' ? 'bg-indigo-50 text-indigo-700 border-indigo-100' : 'bg-blue-50 text-blue-700 border-blue-100'
                             }`}>
                                {entity.relationship}
                             </span>
                          </div>
                       ))}
                    </div>
                 )}
              </div>

              <div className="bg-white p-10 rounded-[3rem] border border-slate-200 shadow-sm">
                 <div className="flex items-center justify-between mb-8">
                    <h3 className="text-xs font-black text-slate-900 uppercase tracking-widest flex items-center">
                      <History size={16} className="mr-2 text-blue-600" /> Renewal History & Audit Ledger
                    </h3>
                    <button className="text-[10px] font-black text-blue-600 uppercase tracking-widest hover:underline">Download full audit</button>
                 </div>

                 <div className="space-y-4">
                    {detailedEntity.renewalHistory.length === 0 ? (
                       <div className="py-12 text-center opacity-40">
                          <History size={32} className="mx-auto mb-2" />
                          <p className="text-[10px] font-black uppercase tracking-widest">No prior renewal cycles recorded</p>
                       </div>
                    ) : (
                       detailedEntity.renewalHistory.map((h, i) => (
                          <div key={i} className="flex items-center justify-between p-4 border-b border-slate-50 last:border-0 hover:bg-slate-50 transition-colors rounded-xl px-6">
                             <div className="flex items-center space-x-6">
                                <span className="text-[11px] font-black text-slate-400 uppercase tracking-widest">{h.date}</span>
                                <div className="flex items-center space-x-2">
                                   <RefreshCw size={14} className="text-blue-500" />
                                   <span className="text-sm font-bold text-slate-800">{h.type} Renewal</span>
                                </div>
                             </div>
                             <div className="flex items-center space-x-2">
                                <CheckCircle2 size={14} className="text-emerald-500" />
                                <span className="text-[10px] font-black uppercase text-emerald-600">{h.status}</span>
                             </div>
                          </div>
                       ))
                    )}
                 </div>
              </div>
           </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 animate-in slide-in-from-right duration-500 relative min-h-full pb-32">
      {/* Header Section */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
        <div className="flex items-center space-x-4">
          <button onClick={onBack} className="p-3 bg-white border border-slate-200 rounded-2xl hover:bg-slate-100 transition-all text-slate-600 shadow-sm">
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="text-2xl font-black text-slate-900 tracking-tight">LEI Global Registry</h1>
            <p className="text-sm text-slate-500 font-medium">Monitoring Legal Entity Identifiers for global compliance.</p>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          <button className="bg-white border border-slate-200 text-slate-700 px-5 py-2.5 rounded-2xl text-xs font-black uppercase tracking-widest hover:bg-slate-50 shadow-sm transition-all">
            <Download size={16} />
          </button>
          <button 
            onClick={() => setIsRegistering(true)}
            className="bg-blue-600 text-white px-6 py-2.5 rounded-2xl text-xs font-black uppercase tracking-widest flex items-center space-x-2 hover:bg-blue-700 transition-all shadow-lg shadow-blue-500/20 active:scale-95"
          >
            <Plus size={16} />
            <span>Register New LEI</span>
          </button>
        </div>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <RegistryStat label="Total Managed" value={MOCK_LEIS.length} icon={<Layers size={18} />} color="blue" />
        <RegistryStat label="Active Issued" value={MOCK_LEIS.filter(l => l.status === 'Issued').length} icon={<ShieldCheck size={18} />} color="emerald" />
        <RegistryStat label="Expiring 30d" value={MOCK_LEIS.filter(l => l.days <= 30 && l.days > 0).length} icon={<Zap size={18} />} color="amber" />
        <RegistryStat label="Critical Renewal" value={MOCK_LEIS.filter(l => l.status !== 'Issued').length} icon={<CreditCard size={18} />} color="rose" />
      </div>

      {/* Main Table */}
      <div className="bg-white rounded-[2rem] border border-slate-200 shadow-sm overflow-hidden">
        <div className="p-6 border-b border-slate-100 flex flex-col md:flex-row items-center justify-between bg-slate-50/50 gap-4">
          <div className="flex items-center space-x-4 w-full md:w-auto">
             <div className="flex items-center space-x-2 bg-white px-4 py-2.5 rounded-2xl border border-slate-200 w-full md:w-96 shadow-inner focus-within:ring-4 focus-within:ring-blue-500/10 transition-all">
               <Search size={16} className="text-slate-400" />
               <input type="text" placeholder="LEI Code or Entity Legal Name..." className="text-sm outline-none w-full font-medium" />
             </div>
          </div>
          <div className="flex items-center space-x-2">
            <button className="p-2.5 border border-slate-200 rounded-xl hover:bg-white hover:shadow-sm text-slate-500 transition-all">
              <Filter size={18} />
            </button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead className="bg-slate-50/80 text-[10px] uppercase tracking-widest font-black text-slate-400 border-b border-slate-100">
              <tr>
                <th className="px-6 py-5 w-16">
                  <button 
                    onClick={toggleSelectAll}
                    className={`w-6 h-6 rounded-lg border-2 flex items-center justify-center transition-all shadow-sm ${
                      selectedIds.size === MOCK_LEIS.length 
                        ? 'bg-blue-600 border-blue-600 text-white' 
                        : 'bg-white border-slate-200 hover:border-blue-500'
                    }`}
                  >
                    {selectedIds.size === MOCK_LEIS.length && <Check size={14} strokeWidth={4} />}
                  </button>
                </th>
                <th className="px-6 py-5">Legal Entity Dossier</th>
                <th className="px-6 py-5">LEI Code</th>
                <th className="px-6 py-5">Registry Expiry</th>
                <th className="px-6 py-5 text-center">Status</th>
                <th className="px-6 py-5 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {MOCK_LEIS.map((entity) => (
                <tr 
                  key={entity.id} 
                  onClick={() => setDetailedEntity(entity)}
                  className={`transition-all group cursor-pointer ${selectedIds.has(entity.id) ? 'bg-blue-50/40' : 'hover:bg-slate-50/50'}`}
                >
                  <td className="px-6 py-5">
                    <button 
                      onClick={(e) => toggleSelect(entity.id, e)}
                      className={`w-6 h-6 rounded-lg border-2 flex items-center justify-center transition-all shadow-sm ${
                        selectedIds.has(entity.id) 
                          ? 'bg-blue-600 border-blue-600 text-white' 
                          : 'bg-white border-slate-200 group-hover:border-blue-400'
                      }`}
                    >
                      {selectedIds.has(entity.id) && <Check size={14} strokeWidth={4} />}
                    </button>
                  </td>
                  <td className="px-6 py-5">
                    <div className="flex items-center space-x-4">
                      <div className="w-10 h-10 rounded-2xl bg-slate-100 flex items-center justify-center text-slate-400 group-hover:bg-white group-hover:text-blue-600 transition-all shadow-sm">
                        <Calendar size={18} />
                      </div>
                      <span className="text-sm font-black text-slate-900 leading-tight">{entity.name}</span>
                    </div>
                  </td>
                  <td className="px-6 py-5 font-mono text-xs text-slate-500 font-semibold">{entity.code}</td>
                  <td className="px-6 py-5">
                    <div className="flex flex-col">
                      <span className="text-sm font-black text-slate-700">{entity.expiry}</span>
                      <span className={`text-[10px] font-black uppercase tracking-widest mt-0.5 ${entity.days < 0 ? 'text-rose-600' : entity.days < 30 ? 'text-amber-600' : 'text-slate-400'}`}>
                        {entity.days < 0 ? 'Critical Expired' : `${entity.days} Days Remaining`}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-5 text-center">
                    <div className={`inline-flex items-center px-3 py-1 rounded-xl text-[10px] font-black uppercase tracking-widest shadow-sm border ${
                      entity.status === 'Issued' ? 'bg-emerald-50 text-emerald-700 border-emerald-100' : 
                      entity.status === 'Lapsed' ? 'bg-amber-50 text-amber-700 border-amber-100' : 'bg-rose-50 text-rose-700 border-rose-100'
                    }`}>
                      {entity.status}
                    </div>
                  </td>
                  <td className="px-6 py-5 text-right">
                    <div className="flex justify-end space-x-1">
                      <button 
                        onClick={(e) => { e.stopPropagation(); setProcessingStep(1); setTimeout(() => { setProcessingStep(null); setShowSuccess(true); }, 1500); }}
                        className="p-2 text-blue-600 hover:bg-white rounded-xl shadow-sm border border-transparent hover:border-slate-100 transition-all"
                      >
                        <RefreshCw size={18} />
                      </button>
                      <button className="p-2 text-slate-400 hover:text-slate-900 hover:bg-white rounded-xl shadow-sm border border-transparent hover:border-slate-100 transition-all">
                        <ChevronRight size={18} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Floating Action Bar */}
      {selectedIds.size > 0 && !showConfirm && !processingStep && !showSuccess && !isRegistering && !detailedEntity && (
        <div className="fixed bottom-8 left-1/2 -translate-x-1/2 z-40 animate-in slide-in-from-bottom-10 duration-500">
          <div className="bg-slate-900/90 backdrop-blur-md text-white px-8 py-4 rounded-[2rem] shadow-2xl flex items-center space-x-8 border border-white/10">
             <div className="flex items-center space-x-4">
                <div className="w-10 h-10 bg-blue-600 rounded-2xl flex items-center justify-center font-black shadow-lg shadow-blue-500/20">
                  {selectedIds.size}
                </div>
                <div>
                   <p className="text-sm font-black tracking-tight leading-none mb-1">Entities Selected</p>
                   <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest leading-none">Global Registry Queue</p>
                </div>
             </div>
             <div className="h-8 w-px bg-white/10"></div>
             <div className="flex items-center space-x-4">
                <button onClick={() => setSelectedIds(new Set())} className="text-xs font-black uppercase tracking-widest text-slate-400 hover:text-white transition-colors">
                  Deselect
                </button>
                <button 
                  onClick={startRenewalFlow}
                  className="bg-white text-slate-900 px-6 py-2.5 rounded-2xl text-[11px] font-black uppercase tracking-widest hover:bg-blue-500 hover:text-white transition-all active:scale-95 flex items-center space-x-2"
                >
                  <RefreshCw size={14} className="animate-spin-slow" />
                  <span>Initiate Bulk Renewal</span>
                </button>
             </div>
          </div>
        </div>
      )}

      {/* Slide-over Registration Form */}
      {isRegistering && (
        <div className="fixed inset-0 z-50 overflow-hidden">
          <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-300" onClick={() => setIsRegistering(false)}></div>
          <div className="absolute inset-y-0 right-0 max-w-2xl w-full bg-white shadow-2xl flex flex-col animate-in slide-in-from-right duration-500 rounded-l-[3rem] border-l border-slate-100">
            <div className="p-10 border-b border-slate-100 flex items-center justify-between bg-slate-50/50 rounded-tl-[3rem]">
              <div className="flex items-center space-x-4">
                <div className="p-3 bg-blue-600 text-white rounded-2xl shadow-lg shadow-blue-200">
                  <Globe size={24} />
                </div>
                <div>
                  <h2 className="text-2xl font-black text-slate-900 tracking-tight">New LEI Registration</h2>
                  <p className="text-xs text-slate-500 font-bold uppercase tracking-widest mt-1">GLEIF Compliant Issuance Node</p>
                </div>
              </div>
              <button onClick={() => setIsRegistering(false)} className="p-3 hover:bg-white rounded-2xl text-slate-400 hover:text-slate-900 shadow-sm transition-all border border-transparent hover:border-slate-100">
                <XCircle size={24} />
              </button>
            </div>
            
            <div className="flex-1 overflow-y-auto p-10 space-y-10">
              {/* Entity Core Info */}
              <div className="space-y-6">
                <h3 className="text-xs font-black text-blue-600 uppercase tracking-widest flex items-center">
                  <Building2 size={14} className="mr-2" /> Legal Entity Identity
                </h3>
                <div className="grid grid-cols-1 gap-6">
                   <div className="space-y-2">
                     <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Entity Legal Name</label>
                     <div className="relative">
                        <Building2 className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-300" size={18} />
                        <input 
                          type="text" 
                          value={formName}
                          onChange={(e) => setFormName(e.target.value)}
                          className="w-full pl-12 pr-4 py-3.5 bg-slate-50 border border-slate-200 rounded-2xl text-sm font-black focus:bg-white focus:ring-4 focus:ring-blue-500/10 outline-none transition-all" 
                          placeholder="e.g. Acme Global Holdings S.A." 
                        />
                     </div>
                   </div>

                   <div className="grid grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Legal Jurisdiction</label>
                        <select 
                          value={formJurisdiction}
                          onChange={(e) => setFormJurisdiction(e.target.value)}
                          className="w-full px-4 py-3.5 bg-slate-50 border border-slate-200 rounded-2xl text-sm font-bold focus:bg-white focus:ring-4 focus:ring-blue-500/10 outline-none transition-all appearance-none"
                        >
                          <option value="GB">United Kingdom (GB)</option>
                          <option value="US">United States (US)</option>
                          <option value="SG">Singapore (SG)</option>
                          <option value="DE">Germany (DE)</option>
                          <option value="FR">France (FR)</option>
                        </select>
                      </div>
                      <div className="space-y-2">
                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Local Registration No.</label>
                        <input 
                          type="text" 
                          value={formRegNo}
                          onChange={(e) => setFormRegNo(e.target.value)}
                          className="w-full px-4 py-3.5 bg-slate-50 border border-slate-200 rounded-2xl text-sm font-mono focus:bg-white focus:ring-4 focus:ring-blue-500/10 outline-none transition-all" 
                          placeholder="10293845" 
                        />
                      </div>
                   </div>
                </div>
              </div>

              {/* Address Section */}
              <div className="space-y-6">
                <h3 className="text-xs font-black text-blue-600 uppercase tracking-widest flex items-center">
                  <MapPin size={14} className="mr-2" /> Registered Address
                </h3>
                <div className="grid grid-cols-1 gap-4">
                  <input type="text" className="w-full px-4 py-3.5 bg-slate-50 border border-slate-200 rounded-2xl text-sm font-medium focus:bg-white focus:ring-4 focus:ring-blue-500/10 outline-none transition-all" placeholder="Street Address" />
                  <div className="grid grid-cols-2 gap-4">
                    <input type="text" className="w-full px-4 py-3.5 bg-slate-50 border border-slate-200 rounded-2xl text-sm font-medium focus:bg-white focus:ring-4 focus:ring-blue-500/10 outline-none transition-all" placeholder="City" />
                    <input type="text" className="w-full px-4 py-3.5 bg-slate-50 border border-slate-200 rounded-2xl text-sm font-medium focus:bg-white focus:ring-4 focus:ring-blue-500/10 outline-none transition-all" placeholder="Postcode" />
                  </div>
                </div>
              </div>

              {/* Legal Structure */}
              <div className="space-y-6">
                <h3 className="text-xs font-black text-blue-600 uppercase tracking-widest flex items-center">
                  <ShieldAlert size={14} className="mr-2" /> Parent Relationship
                </h3>
                <div className="p-6 bg-blue-50/50 rounded-[2rem] border border-blue-100 flex flex-col space-y-4">
                  <p className="text-[10px] text-blue-800 font-bold leading-relaxed uppercase tracking-tight">
                    GLEIF requires declaration of level 1 and level 2 parents. If the entity has no legal parents, please select "No Parent" below.
                  </p>
                  <div className="flex space-x-3">
                    <button className="flex-1 py-3 bg-white border border-blue-200 rounded-xl text-[10px] font-black uppercase text-blue-600 shadow-sm hover:bg-blue-600 hover:text-white transition-all">Consolidated Parent</button>
                    <button className="flex-1 py-3 bg-blue-600 text-white border border-blue-600 rounded-xl text-[10px] font-black uppercase shadow-lg shadow-blue-200">No Parent (Natural)</button>
                  </div>
                </div>
              </div>

              {/* Fee Notice */}
              <div className="mt-10 p-6 bg-slate-900 rounded-[2.5rem] text-white flex items-center justify-between">
                <div>
                   <p className="text-[9px] font-black text-slate-500 uppercase tracking-[0.2em] mb-1">Standard Issuance Fee</p>
                   <p className="text-2xl font-black">$69.00 <span className="text-xs text-slate-500 font-bold">/ YEAR</span></p>
                </div>
                <div className="text-right">
                   <p className="text-[9px] font-black text-emerald-400 uppercase tracking-widest mb-1">Instant Allocation</p>
                   <p className="text-[10px] text-slate-400 font-medium">Auto-renew active</p>
                </div>
              </div>
            </div>

            <div className="p-10 border-t border-slate-100 bg-slate-50 flex space-x-4 rounded-bl-[3rem]">
              <button onClick={() => setIsRegistering(false)} className="flex-1 py-4 bg-white border border-slate-200 rounded-2xl text-[11px] font-black uppercase tracking-widest text-slate-600 hover:bg-slate-100 transition-all active:scale-95 shadow-sm">
                Discard
              </button>
              <button 
                onClick={executeRegistration}
                disabled={!formName}
                className="flex-1 py-4 bg-slate-900 text-white rounded-2xl text-[11px] font-black uppercase tracking-widest hover:bg-slate-800 shadow-xl shadow-slate-200 transition-all active:scale-95 disabled:opacity-50"
              >
                Execute Registration
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Step Modal */}
      {showConfirm && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-xl animate-in fade-in duration-300" onClick={() => setShowConfirm(false)}></div>
          <div className="relative bg-white rounded-[3rem] shadow-2xl p-10 max-w-lg w-full animate-in zoom-in-95 duration-200 border border-slate-100">
            <div className="flex items-center space-x-4 mb-8">
               <div className="w-14 h-14 bg-amber-50 text-amber-600 rounded-2xl flex items-center justify-center shadow-sm">
                  <AlertCircle size={28} />
               </div>
               <div>
                 <h3 className="text-2xl font-black text-slate-900 tracking-tight">Review Bulk Renewal</h3>
                 <p className="text-xs text-slate-400 font-bold uppercase tracking-widest">Verification & Compliance Sync</p>
               </div>
            </div>

            <div className="bg-slate-50 rounded-3xl p-6 mb-8 max-h-48 overflow-y-auto space-y-3 border border-slate-100">
               {selectedEntities.map(e => (
                 <div key={e.id} className="flex justify-between items-center py-2 border-b border-slate-100 last:border-0">
                    <div className="flex flex-col">
                       <span className="text-xs font-black text-slate-900">{e.name}</span>
                       <span className="text-[10px] font-mono text-slate-400 uppercase tracking-tight">{e.code}</span>
                    </div>
                    <span className="text-[10px] font-black bg-blue-50 text-blue-600 px-2 py-0.5 rounded uppercase tracking-widest">Sync Active</span>
                 </div>
               ))}
            </div>

            <div className="flex items-center space-x-4">
              <button 
                onClick={() => setShowConfirm(false)}
                className="flex-1 py-4 bg-white border border-slate-200 text-slate-700 rounded-2xl text-[11px] font-black uppercase tracking-widest hover:bg-slate-50 transition-all"
              >
                Cancel Request
              </button>
              <button 
                onClick={executeRenewal}
                className="flex-1 py-4 bg-slate-900 text-white rounded-2xl text-[11px] font-black uppercase tracking-widest hover:bg-slate-800 transition-all shadow-xl shadow-slate-200 flex items-center justify-center space-x-2"
              >
                <span>Process Renewal</span>
                <ArrowRight size={14} />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Success Modal */}
      {showSuccess && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-xl animate-in fade-in duration-300" onClick={resetSelection}></div>
          <div className="relative bg-white rounded-[3rem] shadow-2xl p-10 max-w-md w-full text-center animate-in zoom-in-95 duration-200 border border-slate-100">
            <div className="w-20 h-20 bg-emerald-100 text-emerald-600 rounded-[2rem] flex items-center justify-center mx-auto mb-8 shadow-xl shadow-emerald-100">
              <CheckCircle2 size={40} />
            </div>
            <h3 className="text-3xl font-black text-slate-900 mb-4 tracking-tight">Operation Complete</h3>
            <p className="text-sm text-slate-500 font-medium mb-8 leading-relaxed">
              Successfully updated registry status in the global registry node. Documentation has been dispatched to your primary email.
            </p>
            
            <button 
              onClick={resetSelection}
              className="w-full py-4 bg-slate-900 text-white rounded-[1.5rem] text-[11px] font-black uppercase tracking-widest hover:bg-slate-800 transition-all active:scale-95"
            >
              Back to Registry
            </button>
          </div>
        </div>
      )}

      {/* Processing State Overlay */}
      {processingStep !== null && (
        <div className="fixed inset-0 z-[70] flex flex-col items-center justify-center bg-white/70 backdrop-blur-sm animate-in fade-in duration-500">
          <div className="bg-slate-900 text-white px-10 py-12 rounded-[3rem] shadow-2xl flex flex-col items-center space-y-8 border border-white/10 max-w-sm w-full">
            <div className="relative">
              <Loader2 size={56} className="animate-spin text-blue-400" />
              <div className="absolute inset-0 flex items-center justify-center">
                 <Zap size={18} className="text-blue-400 animate-pulse" />
              </div>
            </div>
            <div className="text-center space-y-2">
               <p className="text-xl font-black tracking-tight">
                 {processingStep === 1 ? 'Validating Entity' : 
                  processingStep === 2 ? 'Allocating LEI Slot' : 
                  'Contacting GLEIF Node'}
               </p>
               <p className="text-[10px] text-slate-400 font-black uppercase tracking-[0.2em] animate-pulse">
                 Synchronizing Global Database...
               </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
